import {IApiEndpoints} from "../typings/apiEndpoints";
import {IApiTeamMember, IApiTeam, IApiTeamMembership, IApiMembership} from "../typings/apiResponse";
import {ITeamEditableFields} from "../league/Team";
import * as angular from "angular";

export class TeamResource {
    public static $inject = ["$http", "ApiEndpoints"];
    constructor(private $http: angular.IHttpService, private ApiEndpoints: IApiEndpoints) {

    }

    /**
     * Returns a list of users in a team and their roles and permissions. If the embedGameaccount parameter is set
     * to a valid game account key the game account of all team members will be included if available.
     *
     * @param teamId
     * @param embedGameaccount
     *
     * @returns {Promise<IApiTeamMember[]>}
     */
    public getTeamMembers(teamId: string, embedGameaccount?: string): angular.IPromise<IApiTeamMember[]> {
        return this.$http.get(this.ApiEndpoints.team + '/' + teamId + '/members', {params: {embed_gameaccount: embedGameaccount}}).then(
            (response: angular.IHttpPromiseCallbackArg<IApiTeamMember[]>) => {
                var teamMembers: IApiTeamMember[] = [];

                angular.forEach(response.data, (apiTeamMember: IApiTeamMember) => {
                    teamMembers.push(apiTeamMember);
                });

                return teamMembers;
            }
        );
    }

    /**
     * @param teamId
     * @returns {Promise<IApiTeam>}
     */
    public getTeam(teamId: string): angular.IPromise<IApiTeam> {
        return this.$http.get(this.ApiEndpoints.team + '/' + teamId).then(
            function (response: angular.IHttpPromiseCallbackArg<IApiTeam>) {
                return response.data;
            }
        );
    }

    /**
     * @returns {Promise<IApiTeamMembership[]>}
     */
    public getUserTeams(): angular.IPromise<IApiTeamMembership[]> {
        return this.$http.get(this.ApiEndpoints.userTeam).then(
            function (response: angular.IHttpPromiseCallbackArg<IApiTeamMembership[]>) {
                return response.data;
            }
        );
    }

    /**
     * @param teamId
     * @returns {Promise<IApiTeamMembership>}
     */
    public getUserTeam(teamId: string): angular.IPromise<IApiTeamMembership> {
        return this.$http.get(this.ApiEndpoints.userTeam + '/' + teamId).then(
            function (response: angular.IHttpPromiseCallbackArg<IApiTeamMembership>) {
                return response.data;
            }
        );
    }

    /**
     * @param teamId
     * @param content
     * @returns {IHttpPromise<any>}
     */
    public setShortDescription(teamId: string, content: string): angular.IHttpPromise<any> {
        return this.$http.put(this.ApiEndpoints.team + '/' + teamId + '/shortdescription', content);
    }

    /**
     *
     * @param teamId
     * @param format
     * @returns {Promise<string>}
     */
    public getShortDescription(teamId: string, format?: string): angular.IPromise<string> {
        return this.$http.get(this.ApiEndpoints.team + '/' + teamId + '/shortdescription', {
            params: {
                format: format
            }
        }).then(
            function (response: angular.IHttpPromiseCallbackArg<string>) {
                return response.data;
            }
        );
    }

    /**
     * @param teamId
     * @param content
     * @returns {IHttpPromise<any>}
     */
    public setDescription(teamId: string, content: string): angular.IHttpPromise<any> {
        return this.$http.put(this.ApiEndpoints.team + '/' + teamId + '/description', content);
    }

    /**
     * @param teamId
     * @param format
     * @returns {Promise<string>}
     */
    public getDescription(teamId: string, format?: string): angular.IPromise<string> {
        return this.$http.get(this.ApiEndpoints.team + '/' + teamId + '/description', {
            params: {
                format: format
            }
        }).then(
            function (response: angular.IHttpPromiseCallbackArg<string>) {
                return response.data;
            }
        );
    }

    /**
     * @param name
     * @param password
     * @returns {Promise<IApiTeamMembership>}
     */
    public createTeam(name: string, password?: string): angular.IPromise<IApiTeamMembership> {
        return this.$http.post(this.ApiEndpoints.userTeam, {
            name: name,
            password: password
        }).then(
            function (response: angular.IHttpPromiseCallbackArg<IApiTeamMembership>) {
                return response.data;
            }
        );
    }

    /**
     * @param teamId
     * @param password
     * @returns {Promise<IApiTeamMembership>}
     */
    public joinTeam(teamId: string, password?: string): angular.IPromise<IApiTeamMembership> {
        return this.$http.post(this.ApiEndpoints.team + '/' + teamId + '/members', {
            password: password
        }).then(
            function (response: angular.IHttpPromiseCallbackArg<IApiTeamMembership>) {
                return response.data;
            }
        );
    }

    /**
     * @param teamId
     * @param fields
     * @returns {Promise<IApiTeamMembership>}
     */
    public editTeam(teamId: string, fields: ITeamEditableFields): angular.IPromise<IApiTeamMembership> {
        return this.$http.put(this.ApiEndpoints.team + '/' + teamId, fields).then(
            function (response: angular.IHttpPromiseCallbackArg<IApiTeamMembership>) {
                return response.data;
            }
        );
    }

    /**
     * @param teamId
     * @param image
     * @returns {Promise<angular.IHttpPromiseCallbackArg<any>>}
     */
    public uploadLogo(teamId: string, image: File): angular.IPromise<angular.IHttpPromiseCallbackArg<any>> {
        var formData = new FormData();
        formData.append('logo', image);

        return this.$http.post(this.ApiEndpoints.team + '/' + teamId + '/logo', formData, {
            transformRequest: angular.identity,
            headers: {'Content-Type': undefined}
        }).then((response: angular.IHttpPromiseCallbackArg<any>) => {
            return response;
        })
    }

    /**
     * @param teamId
     * @param userIdOrEmail Either an userId or an email address
     * @param domain For example: /play or /codworldleague
     * @returns {Promise<angular.IHttpPromiseCallbackArg<any>>}
     */
    public inviteMember(teamId: string, userIdOrEmail: string, domain?: string) {
        var fields: any = {
            domain: domain,
            userId: userIdOrEmail
        };

        if (isNaN(<any> userIdOrEmail)) {
            fields.email = fields.userId;
            delete fields.userId;
        }

        return this.$http.post(this.ApiEndpoints.team + '/' + teamId + '/invitation', fields).then(
            function (response: angular.IHttpPromiseCallbackArg<any>) {
                return response;
            }
        );
    }

    /**
     * @param teamId
     * @param userId
     * @returns {Promise<angular.IHttpPromiseCallbackArg<any>>}
     */
    public kickMember(teamId: string, userId: string) {
        return this.$http.delete(this.ApiEndpoints.team + '/' + teamId + '/members/' + userId).then(
            function (response: angular.IHttpPromiseCallbackArg<any>) {
                return response;
            }
        );
    }

    /**
     * @param teamId
     * @param userId
     * @param role
     * @param permission
     * @returns {Promise<IApiMembership>}
     */
    public editMembership(teamId: string, userId: string, role?: string, permission?: string) {
        return this.$http.put(this.ApiEndpoints.team + '/' + teamId + '/members/' + userId + '/membership', {
            role: role,
            permission: permission
        }).then(
            function (response: angular.IHttpPromiseCallbackArg<IApiMembership>) {
                return response.data;
            }
        );
    }

    /**
     * @param teamIds
     * @param fields
     * @returns {Promise<IApiTeam[]>}
     */
    public getTeams(teamIds: string[], fields?: string[]): angular.IPromise<IApiTeam[]> {
        const fieldsParam = fields ? '&fields=' + fields.join(',') : '';
        return this.$http.get(this.ApiEndpoints.team + '?ids=' + teamIds.join(',') + fieldsParam).then(
            (response: angular.IHttpPromiseCallbackArg<IApiTeam[]>) => {
                return response.data;
            }
        );
    }

    /**
     * Delete a team.
     *
     * @param teamId Id of the team to delete.
     *
     * @returns A promise of the request.
     */
    public deleteTeam(teamId: string): angular.IPromise<any> {
        return this.$http.delete(`${this.ApiEndpoints.team}/${teamId}`);
    }
}